import { Application, Graphics } from 'pixi.js';
import { Viewport } from 'pixi-viewport';
import { Vector2, EventDispatcher, CompressedPixelFormat } from 'three';
import { EVENT_NEW, EVENT_DELETED, EVENT_LOADED, EVENT_2D_SELECTED, EVENT_NEW_ROOMS_ADDED, EVENT_KEY_RELEASED, EVENT_KEY_PRESSED, EVENT_WALL_2D_CLICKED, EVENT_CORNER_2D_CLICKED, EVENT_ROOM_2D_CLICKED, EVENT_NOTHING_2D_SELECTED, EVENT_MOVED, EVENT_MODE_RESET, EVENT_EXTERNAL_FLOORPLAN_LOADED, EVENT_NEW_2D_ITEM, EVENT_DOOR_2D_CLICKED, EVENT_FLOOR_ITEM_2D_CLICKED, EVENT_OPEN_WINDOW_SET } from '../core/events';
import { Grid2D } from './Grid2d';
import { CornerView2D } from './View/CornerView2D';
import { WallView2D } from './View/WallView2D';
import { WallItemView2D } from "./View/WallItemView2D";
import { RoomView2D } from './View/RoomView2D';
import { Dimensioning } from '../core/dimensioning';
import { KeyboardListener2D } from './KeyboardManager2D';
import { Configuration, snapToGrid, snapTolerance, viewBounds } from '../core/configuration';
import { IS_TOUCH_DEVICE } from '../../DeviceInfo';
import { CornerGroupTransform2D } from './CornerGroupTransform2D';
import Room from '../model/room';
import { BoundaryView2D } from './View/BoundaryView2D';
import { ParametricBaseDoor } from '../parametrics/doors/ParametricBaseDoor';
import { InWallFloorItem } from '../items/in_wall_floor_item';
import { InWallItem } from '../items/in_wall_item';
import { Viewer3D } from '../viewer3d/Viewer3d';
import { Utils } from '../core/utils';
import { Vector3 } from 'three/build/three.module';
import { Factory } from '../items/factory';
import {objects3DList} from './objects3D.js'
import { FloorItem } from '../items/floor_item';
import { FloorItemView2D } from './View/FloorItemView';
import swal from 'sweetalert';
import { TemporaryWall } from './TemporaryView/TemporaryWall';
import { TemporaryWallItem } from './TemporaryView/TemporaryWallItem';
import { TemporaryFloorItem } from './TemporaryView/TemporaryFloorItem';

export const floorplannerModes = { MOVE: 0, DRAW: 1, EDIT_ISLANDS: 2, DOORADD: 3, WINDOWADD: 4, FLOORITEMADD: 5};

export class Viewer2D extends Application {
    constructor(canvasHolder, floorplan, options) {
        const { pixiAppOptions, pixiViewportOptions } = options;
        const pixiDefalultAppOpts = {
            width: 512, 
            height: 512,
            resolution: window.devicePixelRatio || 2,
            antialias: true,
            transparent: false,
        };
        // super({width: 512, height: 512});
        super(Object.assign(pixiDefalultAppOpts, pixiAppOptions));
        this.__eventDispatcher = new EventDispatcher();

        let opts = { 
            'corner-radius': 20, 
            'boundary-point-radius': 5.0,
            'boundary-line-thickness': 1.0,
            'boundary-point-color':'#D3D3D3',
            'boundary-line-color':'#F3F3F3',
            pannable: true, 
            zoomable: true, 
            dimlinecolor: '#3EDEDE', 
            dimarrowcolor: '#000000', 
            dimtextcolor: '#000000', 
            scale: true, 
            rotate: true, 
            translate: true,
            resize: true,
        };

        for (var opt in opts) {
            if (opts.hasOwnProperty(opt) && options.hasOwnProperty(opt)) {
                opts[opt] = options[opt];
            }
        }

        // console.log('VIEWER 2D ::: ', opts);
        this.__mode = floorplannerModes.MOVE;
        this.__canvasHolder = document.getElementById(canvasHolder);
        this.__floorplan = floorplan;
        this.__options = opts;

        this.__lastNode = null;
        this.__tempWall = new TemporaryWall();
        // temporary wall item
        this.tempWallitem = new TemporaryWallItem();

        this.tempFloorItem = new TemporaryFloorItem();

        this.__corners2d = [];
        this.__walls2d = [];
        this.__rooms2d = [];
        this.__entities2D = [];

        this.__externalCorners2d = [];
        this.__externalWalls2d = [];
        this.__externalRooms2d = [];
        this.__externalEntities2d = [];

        this.__worldWidth = 3000;
        this.__worldHeight = 3000;
        this.__currentWidth = 500;
        this.__currentHeight = 500;
        this.__currentSelection = null;

        this.__zoomedEvent = this.__zoomed.bind(this);
        this.__pannedEvent = this.__panned.bind(this);
        this.__selectionMonitorEvent = this.__selectionMonitor.bind(this);
        this.__cornerMovedEvent = this.__cornerMoved.bind(this);

        //bind 
        this.onMouseDown = this.onMouseDown.bind(this);
        this.onMouseUp = this.onMouseUp.bind(this);
        this.onMouseMove = this.onMouseMove.bind(this);
        this.doorAddModeMouseDown = this.doorAddModeMouseDown.bind(this);
        this.doorAddModeMouseUp = this.doorAddModeMouseUp.bind(this);
        this.doorAddModeMouseMove = this.doorAddModeMouseMove.bind(this);

        this.__drawModeMouseDownEvent = this.__drawModeMouseDown.bind(this);
        this.__drawModeMouseUpEvent = this.__drawModeMouseUp.bind(this);
        this.__drawModeMouseMoveEvent = this.__drawModeMouseMove.bind(this);

        this.__redrawFloorplanEvent = this.__redrawFloorplan.bind(this);
        this.__drawExternalFloorplanEvent = this.__drawExternalFloorplan.bind(this);
        this.__windowResizeEvent = this._handleWindowResize.bind(this);
        this.__resetFloorplanEvent = this.__resetFloorplan.bind(this);

        this.__floorplanLoadedEvent = this.__center.bind(this);

        const pixiViewportDefaultOpts = {
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            worldWidth: this.__worldWidth,
            worldHeight: this.__worldHeight,
            interaction: this.renderer.plugins.interaction,
            passiveWheel: false,
        };

        this.__floorplanContainer = new Viewport(Object.assign(pixiViewportDefaultOpts, pixiViewportOptions));
        this.__floorplanContainer.scale.x = this.__floorplanContainer.scale.y = 1;
        this.__tempWallHolder = new Graphics();
        // needed to ensure correct coordinates of the temporary
        // wall item
        this.tempWallItemHolder = new Graphics();
        // needed to ensure correct coordinates of the temporary
        // floor item
        this.tempFloorItemHolder = new Graphics();

        this.__snapToGrid = false;
        this.__keyboard = new KeyboardListener2D();
        this.__keyListenerEvent = this.__keyListener.bind(this);        

        let origin = new Graphics();
        this.__floorplanElementsHolder = new Graphics();
        this.__boundaryHolder = new Graphics();
        this.__grid2d = new Grid2D(this.view, options);
        this.__boundaryRegion2D = null;
        this.__groupTransformer = new CornerGroupTransform2D(this.__floorplan, this.__options);
        this.__groupTransformer.visible = false;
        this.__groupTransformer.selected = null;

        origin.beginFill(0xFF0000);
        origin.drawCircle(0, 0, 5);

        this.__floorplanContainer.position.set(window.innerWidth * 0.5, window.innerHeight * 0.5);

        this.renderer.backgroundColor = 0xFFFFFF;
        this.renderer.autoResize = true;

        this.__tempWall.visible = false;

        this.__floorplanContainer.addChild(this.__grid2d);
        this.__floorplanContainer.addChild(this.__boundaryHolder);
        // this.__floorplanContainer.addChild(this.__tempWall);
        this.__floorplanContainer.addChild(origin);
        this.__floorplanContainer.addChild(this.__floorplanElementsHolder);
        this.__floorplanContainer.addChild(this.__groupTransformer);

        // adding temporary elements to their corresponding containers
        this.__tempWallHolder.addChild(this.__tempWall);
        this.tempWallItemHolder.addChild(this.tempWallitem);
        this.tempFloorItemHolder.addChild(this.tempFloorItem);

        this.stage.addChild(this.__floorplanContainer);
        this.stage.addChild(this.__tempWallHolder);
        // adding temporary wall item to the scene
        // if it is not in stage it won't show on screen
        this.stage.addChild(this.tempWallItemHolder);
        this.stage.addChild(this.tempFloorItemHolder);

        this.__canvasHolder.appendChild(this.view);

        this.__floorplanContainer.drag().pinch().wheel();
        
        if (!this.__options.pannable) {
            this.__floorplanContainer.plugins.pause('drag');
        }

        if (!this.__options.zoomable) {
            this.__floorplanContainer.plugins.pause('wheel');
            this.__floorplanContainer.plugins.pause('pinch');
        }

        this.__keyboard.addEventListener(EVENT_KEY_RELEASED, this.__keyListenerEvent);
        this.__keyboard.addEventListener(EVENT_KEY_PRESSED, this.__keyListenerEvent);

        this.__floorplanContainer.on('zoomed', this.__zoomedEvent);
        this.__floorplanContainer.on('moved', this.__pannedEvent);
        this.__floorplanContainer.on('clicked', this.__selectionMonitorEvent);

        // mouse event listeners
        this.__floorplanContainer.on('mousedown', this.onMouseDown);
        this.__floorplanContainer.on('mouseup', this.onMouseUp);
        this.__floorplanContainer.on('mousemove', this.onMouseMove);

        // this.__floorplanContainer.on('mousedown', this.__drawModeMouseDownEvent);
        // this.__floorplanContainer.on('mouseup', this.__drawModeMouseUpEvent);
        // this.__floorplanContainer.on('mousemove', this.__drawModeMouseMoveEvent);

        //User touches the screen then emulate the Mouseup event creating a corner
        this.__floorplanContainer.on('touchstart', this.__drawModeMouseUpEvent);
        //User then touch moves and lifts the finger away from the screen. Now create the next corner
        this.__floorplanContainer.on('touchend', this.__drawModeMouseUpEvent);

        //Use touches and drags across the screen then emulate drawing the temporary wall
        this.__floorplanContainer.on('touchmove', this.__drawModeMouseMoveEvent);

        // this.__floorplan.addEventListener(EVENT_UPDATED, (evt) => scope.__redrawFloorplan(evt));

        this.__floorplan.addEventListener(EVENT_LOADED, this.__floorplanLoadedEvent);

        this.__floorplan.addEventListener(EVENT_MODE_RESET, this.__resetFloorplanEvent);
        this.__floorplan.addEventListener(EVENT_NEW, this.__redrawFloorplanEvent);
        this.__floorplan.addEventListener(EVENT_DELETED, this.__redrawFloorplanEvent);

        this.__floorplan.addEventListener(EVENT_NEW_ROOMS_ADDED, this.__redrawFloorplanEvent);

        this.__floorplan.addEventListener(EVENT_EXTERNAL_FLOORPLAN_LOADED, this.__drawExternalFloorplanEvent);


        window.addEventListener('resize', this.__windowResizeEvent);
        window.addEventListener('orientationchange', this.__windowResizeEvent);

        this._handleWindowResize();

        this.__center();

        // adding some necessary information
        this.viewer3D;
        this.floorHelper;
        this.roomHelper;
        this.closestWall = null;
        this.projectionPoint = null;
        this.itemType;

        // window size
        this.windowHeigth = 150;
        this.windowWidth = 150;
    }

    __drawBoundary(){
        // return;
        if(this.__boundaryRegion2D){
            this.__boundaryRegion2D.remove();
        }

        if(this.__floorplan.boundary){
            if(this.__floorplan.boundary.isValid){
                this.__boundaryRegion2D = new BoundaryView2D(this.__floorplan, this.__options, this.__floorplan.boundary);
                this.__boundaryHolder.addChild(this.__boundaryRegion2D);
            }            
        }
    }

    __keyListener(evt) {

        if (evt.type === EVENT_KEY_PRESSED && evt.key === 'Shift') {
            this.__snapToGrid = true;
        }
        if (evt.type === EVENT_KEY_RELEASED && evt.key === 'Shift') {
            this.__snapToGrid = false;
        }
        if (evt.key === 'Escape') {
            this.switchMode(floorplannerModes.MOVE);
        }
    }

    switchMode(mode, viewer3D, roomHelper, floorHelper, it) {
        if(this.__mode === floorplannerModes.EDIT_ISLANDS && mode !== floorplannerModes.EDIT_ISLANDS){
            this.__floorplan.update();
        }
        switch (mode) {
            case floorplannerModes.DRAW:
                this.__mode = floorplannerModes.DRAW;
                this.__floorplanContainer.plugins.pause('drag');
                for (let i = 0; i < this.__entities2D.length; i++) {
                    this.__entities2D[i].interactive = false;
                }
                this.__changeCursorMode();
                this.__tempWall.update();
                this.__tempWall.visible = true;
                this.__groupTransformer.visible = false;
                this.__groupTransformer.selected = null;
                break;
            case floorplannerModes.EDIT_ISLANDS:
                this.__mode = floorplannerModes.EDIT_ISLANDS;
                for (let i = 0; i < this.__entities2D.length; i++) {
                    this.__entities2D[i].interactive = true;
                }
                if (this.__currentSelection instanceof Room) {
                    this.__groupTransformer.visible = true;
                    this.__groupTransformer.selected = this.__currentSelection;
                } else {
                    this.__groupTransformer.visible = false;
                    this.__groupTransformer.selected = null;
                }

                this.__floorplanContainer.plugins.pause('drag');
                for (let i = 0; i < this.__corners2d.length; i++) {
                    this.__corners2d[i].interactive = false;
                }
                for (let i = 0; i < this.__walls2d.length; i++) {
                    this.__walls2d[i].interactive = false;
                }
                this.__changeCursorMode();
                break;
            case floorplannerModes.MOVE:
                this.__mode = floorplannerModes.MOVE;
                for (let i = 0; i < this.__entities2D.length; i++) {
                    this.__entities2D[i].interactive = true;
                }
                this.__tempWall.visible = false;
                this.__groupTransformer.visible = false;
                this.__groupTransformer.selected = null;
                this.__lastNode = null;
                this.__floorplanContainer.plugins.resume('drag');
                this.__changeCursorMode();
                // hide tempWallItem
                this.tempWallitem.visible=false;
                // hide tempFloorItem
                this.tempFloorItem.visible = false;

                // notify blueprint that we are now moving
                this.__eventDispatcher.dispatchEvent({type: "FLOORPLANNER_MODE_MOVE"});
                break;
            case floorplannerModes.DOORADD:
                this.__mode = floorplannerModes.DOORADD;
                this.__floorplanContainer.plugins.pause('drag');
                this.__groupTransformer.visible = false;
                this.__groupTransformer.selected = null;
                this.__changeCursorMode();
                //this.tempWallitem.visible=true;

                //set necessary variables
                this.viewer3D = viewer3D;
                this.roomHelper = roomHelper;
                this.floorHelper = floorHelper;

                // pause wall2d clicked event listener
                for (let i = 0; i < this.__entities2D.length; i++) {
                    this.__entities2D[i].interactive = false;
                }
                break;
            case floorplannerModes.WINDOWADD:
                this.__mode = floorplannerModes.WINDOWADD;
                this.__floorplanContainer.plugins.pause('drag');
                this.__groupTransformer.visible = false;
                this.__groupTransformer.selected = null;
                this.__changeCursorMode();

                //set some necessary variables
                this.viewer3D = viewer3D;
                this.roomHelper = roomHelper;
                this.floorHelper = floorHelper;
                this.__eventDispatcher.dispatchEvent({type: EVENT_OPEN_WINDOW_SET});

                for (let i = 0; i < this.__entities2D.length; i++) {
                    this.__entities2D[i].interactive = false;
                }
                break;
            case floorplannerModes.FLOORITEMADD:
                this.__mode = floorplannerModes.FLOORITEMADD;
                this.__floorplanContainer.plugins.pause('drag');
                this.__groupTransformer.visible = false;
                this.__groupTransformer.selected = null;
                this.__changeCursorMode();

                // set item type for the new item
                this.itemType = it;

                //set some necessary variables
                this.viewer3D = viewer3D;
                this.roomHelper = roomHelper;
                this.floorHelper = floorHelper;

                // disable the entities from being clicked
                for (let i = 0; i < this.__entities2D.length; i++) {
                    this.__entities2D[i].interactive = false;
                }
                break;
            default:
                throw new Error('Unknown Viewer2D mode');
        }
    }

    getWindowDimensions(){
        return {w:this.windowWidth, h:this.windowHeigth};
    }

    setWindowDimensions(width, heigth){
        this.windowWidth=width;
        this.windowHeigth=heigth;
        this.tempWallitem.setDimensions(width, heigth);
    }

    // mouse down 
    onMouseDown(evt){
        switch(this.__mode){
            case floorplannerModes.DRAW:
                this.__drawModeMouseDown(evt);
                break;
            case floorplannerModes.DOORADD:
                this.tempWallitem.setType("door");
                this.doorAddModeMouseDown(evt);
                break;
            case floorplannerModes.WINDOWADD:
                this.tempWallitem.setType("window");
                this.tempWallitem.setDimensions(this.windowWidth, this.windowHeigth);
                this.doorAddModeMouseDown(evt);
                break;
            case floorplannerModes.FLOORITEMADD:
                this.floorItemAddModeMouseDown(evt);
                break;
            default:
                break;
        }
    }

    //mouse up 
    onMouseUp(evt){
        switch(this.__mode){
            case floorplannerModes.DRAW:
                this.__drawModeMouseUp(evt);
                break;
            case floorplannerModes.DOORADD:
                this.doorAddModeMouseUp(evt);
                break;
            case floorplannerModes.WINDOWADD:
                this.windowAddModeMouseUp(evt);
                break;
            case floorplannerModes.FLOORITEMADD:
                this.floorItemAddModeMouseUp(evt);
                break;
            default:
                break;
        }
    }

    //mouse move
    onMouseMove(evt){
        switch(this.__mode){
            case floorplannerModes.DRAW:
                this.__drawModeMouseMove(evt);
                break;
            case floorplannerModes.DOORADD:
                this.doorAddModeMouseMove(evt);
                break;
            case floorplannerModes.WINDOWADD:
                this.doorAddModeMouseMove(evt);
                break;
            case floorplannerModes.FLOORITEMADD:
                this.floorItemAddModeMouseMove(evt);
                break;
            default:
                break;
        }
    }

    __changeCursorMode() {
        let cursor;
        // the various drawing have a value of 3 or higher
        if(this.__mode === floorplannerModes.DRAW || this.__mode >=3){
            cursor = 'crosshair';
        }else{
            cursor = 'pointer';
        };
        this.renderer.plugins.interaction.cursorStyles.crosshair = cursor;
        this.renderer.plugins.interaction.cursorStyles.default = cursor;
        this.renderer.plugins.interaction.setCursorMode(cursor);
    }

    // functin that handles the mouse down event while adding a floor item
    floorItemAddModeMouseDown(evt){
        let mouseCoordinates = evt.data.getLocalPosition(this.__floorplanContainer);

        this.projectionPoint = Dimensioning.pixelToCmVector2D(new Vector2(mouseCoordinates.x, mouseCoordinates.y));

        let dimension = objects3DList[this.itemType.value].size;

        this.tempFloorItem.setDimension(dimension[0]/2, dimension[2]/2);
        // make the tempFloorItem visible
        if(!this.tempFloorItem.visible){
            this.tempFloorItem.visible = !this.tempFloorItem.visible;
            // once again for some reason multiplying by 4 solves the issue
            this.tempFloorItem.update(this.projectionPoint.multiplyScalar(4));
        }
    }

    floorItemAddModeMouseMove(evt){
        if(this.__mode === floorplannerModes.FLOORITEMADD && this.projectionPoint !==null){
            //get current mouse position
            let mouseCoordinates = evt.data.getLocalPosition(this.__floorplanContainer);
            this.projectionPoint = Dimensioning.pixelToCmVector2D(new Vector2(mouseCoordinates.x, mouseCoordinates.y));
            this.projectionPoint.multiplyScalar(4);
            // update temp floor item position
            this.tempFloorItem.update(this.projectionPoint);
        }
    }

    floorItemAddModeMouseUp(evt){
        // create item and add it to the scene
        let itemInfo = objects3DList[this.itemType.value];
        // put item in the correct position
        itemInfo.position[0] = this.projectionPoint.x;
        itemInfo.position[2] = this.projectionPoint.y;

        let newItem = new(Factory.getClass(itemInfo.itemType))(itemInfo, this.roomHelper.__model, itemInfo.id);
        this.roomHelper.__model.addItem(newItem, false);

        //reset projection point
        this.projectionPoint = null;

        //hide temporary floor item and redraw
        this.tempFloorItem.visible = false;
        this.__redrawFloorplan();
    }

    // function that handles the mouse down event while adding a door
    doorAddModeMouseDown(evt){
        let mouseCoordinates = evt.data.getLocalPosition(this.__floorplanContainer);
        let realCoordX = Dimensioning.pixelToCm(mouseCoordinates.x);
        let realCoordY = Dimensioning.pixelToCm(mouseCoordinates.y);
        
        //find closest wall
        let mPoint = new Vector2(realCoordX, realCoordY);
        let wallArray = this.floorHelper.__floorplan.walls;
        let candidateClosestWall = wallArray[0];
        let point = Utils.closestPointOnLine(mPoint, wallArray[0].start._co, wallArray[0].end._co);
        let distance = Utils.distance(point, mPoint);
        for(let i = 1; i< wallArray.length ;++i){
            let candidatePoint = Utils.closestPointOnLine(mPoint, wallArray[i].start._co, wallArray[i].end._co);
            let newDist = Utils.distance(candidatePoint, mPoint)
            if(newDist< distance){
                candidateClosestWall = wallArray[i];
                point=candidatePoint;
                distance = newDist;
            }
        }
        this.projectionPoint = point;
        this.closestWall = candidateClosestWall;

        // make the tempwallItem visible
        if(!this.tempWallitem.visible){
            this.tempWallitem.visible = !this.tempWallitem.visible;
            this.tempWallitem.update(point, candidateClosestWall);
        }
    }

    // model might take a while to load, therefore it is 
    // needed to wait for them before continuing with the
    // creation process
    waitForItemCreation(metadata, item, windowCo, edge){
        metadata.addItem(item);
        setTimeout(()=>{
            item.snapToWall(windowCo, edge.wall, edge.edge);
            item.__followWall();

            // // reset closest wall to null
            this.closestWall = null;
            this.projectionPoint = null;

            // temporary wall item shouldn't be visible anymore
            this.tempWallitem.visible=false;

            // redraw scene
            this.__redrawFloorplan();
        }, 50);
    }

    windowAddModeMouseUp(evt){
        //find the right edge
        let edge;
        let edgeArr = this.viewer3D.edges3d;
        for(let i=0; i<edgeArr.length; ++i){
            if(edgeArr[i].wall.id === this.closestWall.id){
                edge = edgeArr[i];
            }
        }

        //testing stuff
        // Get wall thickness from the edge
        let wallThickness = edge.wall.thickness;
        
        let itemMetadata2 = {
            itemName: "window",
            isParametric: true,
            baseParametricType: "DOOR",
            subParametricData: {
                type: 5,
                frameColor: 15198183,
                doorColor: 15198183,
                doorHandleColor: 15790320,
                glassColor: 8900331,
                frameWidth: this.windowWidth,
                frameHeight: this.windowHeigth,
                frameSize: 0,
                frameThickness: wallThickness,
                doorRatio: 0.5,
                openDirection: "RIGHT",
                handleType: "None",
                isWindow:true
            },
            itemType: 3,
            position: [
                252.10994952514463,
                105,
                10
            ],
            rotation: [
                0,
                0,
                0
            ],
            scale: [
                1,
                1,
                1
            ],
            size: [
                this.windowWidth,
                this.windowHeigth,
                wallThickness
            ],
            fixed: false,
            resizable: false,
            wall: edge.id,
            wallSide: "back",
            wallSurfacePoint: [
                252.10994952514463,
                105,
                10
            ],
        }

        //check whether the window is bigger than the wall
        let wallWidth = Utils.distance(edge.wall.start._co, edge.wall.end._co);
        if(wallWidth< this.windowWidth){
            swal({
                text: "The window is bigger than the wall, please resize.",
                buttons:false,
                timer: 3000
            })
            return;
        }

        let windowCo = new Vector3(this.projectionPoint.x, 138, this.projectionPoint.y);

        if(Utils.doItemsCollide(edge.wall.__inWallItems, new Vector2 (windowCo.x, windowCo.z), this.windowWidth/2 )){
            swal({
                text: "Two objects can't not occupy the same space at the same time"
            });
            this.tempWallitem.visible = false;
            return;
        }

        // metadata
        let metadata = this.roomHelper.__model;

        const item = new InWallItem(itemMetadata2, metadata);

        this.waitForItemCreation(metadata, item, windowCo, edge);
    }

    doorAddModeMouseUp(evt){
        // find the right wall/edge
        let edge;
        for(let i=0; i<this.viewer3D.edges3d.length; ++i){
            if (this.viewer3D.edges3d[i].wall.id === this.closestWall.id){
                edge = this.viewer3D.edges3d[i];
            }
        }
        
        // Get wall thickness from the edge
        let wallThickness = edge.wall.thickness;
            
        // default new door
        let itemMetaData = {
            itemName: "Parametric Door",
            isParametric: true,
            baseParametricType: "DOOR",
            subParametricData: {
                type: 1,
                frameColor: "#E7E7E7",
                doorColor: "#E7E7E7",
                doorHandleColor: '#F0F0F0',
                glassColor: '#87CEEB',
                frameWidth: 100,
                frameHeight: 200,
                frameSize: 5,
                frameThickness: wallThickness,
                doorRatio: 0.5,
                openDirection: "LEFT",
                handleType: "HANDLE_01"
            },
            itemType: 7,
            position: [0,0,0],
            rotation: [0, 0, 0],
            scale: [1, 1, 1],
            size: [100, 200, wallThickness],
            fixed: false,
            resizable: false,
            wall: edge.id
        };

        //metadata
        const metadata = this.roomHelper.__model;

        let doorCo = new Vector3(this.projectionPoint.x, 0, this.projectionPoint.y);

        if(Utils.doItemsCollide(edge.wall.__inWallItems, new Vector2(doorCo.x, doorCo.z), 50)){
            swal({
                text: "Two objects can not occupy the same space"
            });
            // we should stop showing the tempwallitem
            this.tempWallitem.visible = false;
            return;
        }

        // door to add
        const item = new InWallFloorItem(itemMetaData, metadata);

        //add door to the scene
        metadata.addItem(item, false);
        
        //snap the door to the wall
        item.snapToWall(doorCo, edge.wall, edge.edge);
        item.__followWall();

        // reset closest wall to null
        this.closestWall = null;
        this.projectionPoint = null;

        // temporary wall item shouldn't be visible anymore
        this.tempWallitem.visible=false;

        // redraw scene
        this.__redrawFloorplan();
    }

    doorAddModeMouseMove(evt){
        // checking that we are in the right mode and that we have already clicked once
        if ((this.__mode === floorplannerModes.DOORADD|| this.__mode ===floorplannerModes.WINDOWADD) && this.closestWall !== null) {
            // mouse position on canvas
            let mouseCoordinates = evt.data.getLocalPosition(this.__floorplanContainer);
            let realCoordX = Dimensioning.pixelToCm(mouseCoordinates.x);
            let realCoordY = Dimensioning.pixelToCm(mouseCoordinates.y);
            let mPoint = new Vector2(realCoordX, realCoordY);

            // recalculate projection point on the wall
            let projectionPoint = Utils.closestPointOnLine(mPoint, this.closestWall.start._co, this.closestWall.end._co);
            // set projectionPoint to new projection point
            this.projectionPoint = projectionPoint;

            // draw temporary door
            // this.tempWallitem.visible = true;
            this.tempWallitem.update(projectionPoint, this.closestWall);
        }
    }

    __drawModeMouseDown(evt) {
        if (IS_TOUCH_DEVICE) {
            this.__drawModeMouseUp(evt);
        }
    }

    __drawModeMouseUp(evt) {
        if (this.__mode === floorplannerModes.DRAW) {
            let co = evt.data.getLocalPosition(this.__floorplanContainer);
            let cmCo = new Vector2(co.x, co.y);
            cmCo.x = Dimensioning.pixelToCm(cmCo.x);
            cmCo.y = Dimensioning.pixelToCm(cmCo.y);
            if (Configuration.getBooleanValue(snapToGrid) || this.__snapToGrid) {
                cmCo.x = Math.floor(cmCo.x / Configuration.getNumericValue(snapTolerance)) * Configuration.getNumericValue(snapTolerance);
                cmCo.y = Math.floor(cmCo.y / Configuration.getNumericValue(snapTolerance)) * Configuration.getNumericValue(snapTolerance);
            }

            if(this.__floorplan.boundary){
                if(!this.__floorplan.boundary.containsPoint(cmCo.x, cmCo.y)){
                    return;
                }
            }

            // This creates the corner already
            let corner = this.__floorplan.newCorner(cmCo.x, cmCo.y);

            // further create a newWall based on the newly inserted corners
            // (one in the above line and the other in the previous mouse action
            // of start drawing a new wall)
            if (this.__lastNode != null) {
                this.__floorplan.newWall(this.__lastNode, corner);
                this.__floorplan.newWallsForIntersections(this.__lastNode, corner);
                // this.__tempWall.visible = false;
                // this.switchMode(floorplannerModes.MOVE);
            }
            if (corner.mergeWithIntersected() && this.__lastNode != null) {
                this.__tempWall.visible = false;
                this.__lastNode = null;
                this.switchMode(floorplannerModes.MOVE);
            }

            if (this.__lastNode === null && this.__mode === floorplannerModes.DRAW) {
                this.__tempWall.visible = true;
            }

            if (IS_TOUCH_DEVICE && corner && this.__lastNode !== null) {
                this.__tempWall.visible = false;
                this.__lastNode = null;
            } else {
                this.__lastNode = corner;
            }


        }
    }

    __drawModeMouseMove(evt) {
        if (this.__mode === floorplannerModes.DRAW) {
            let co = evt.data.getLocalPosition(this.__floorplanContainer);
            let cmCo = new Vector2(co.x, co.y);
            let lastNode = undefined;
            cmCo.x = Dimensioning.pixelToCm(cmCo.x);
            cmCo.y = Dimensioning.pixelToCm(cmCo.y);
            if (Configuration.getBooleanValue(snapToGrid) || this.__snapToGrid) {
                cmCo.x = Math.floor(cmCo.x / Configuration.getNumericValue(snapTolerance)) * Configuration.getNumericValue(snapTolerance);
                cmCo.y = Math.floor(cmCo.y / Configuration.getNumericValue(snapTolerance)) * Configuration.getNumericValue(snapTolerance);
            }
            if (this.__lastNode !== null) {
                this.__tempWall.update(this.__lastNode, cmCo);
            } else {
                this.__tempWall.update(lastNode, undefined, cmCo);
            }
        }
    }

    __cornerMoved(evt) {
        if (this.__mode === floorplannerModes.EDIT_ISLANDS) {
            return;
        }
        this.__groupTransformer.visible = false;
        this.__groupTransformer.selected = null;
    }

    __selectionMonitor(evt) {
        this.__currentSelection = null;
        this.__groupTransformer.visible = false;
        this.__groupTransformer.selected = null;
        this.__eventDispatcher.dispatchEvent({ type: EVENT_NOTHING_2D_SELECTED });
        for (let i = 0; i < this.__entities2D.length; i++) {
            let entity = this.__entities2D[i];
            if (evt.item !== undefined) {
                if (evt.item === entity) {
                    continue;
                }
            }
            entity.selected = false;
        }
        if (evt.item) {
            let item = null;
            if (evt.item instanceof WallView2D) {
                item = evt.item.wall;
                this.__eventDispatcher.dispatchEvent({ type: EVENT_WALL_2D_CLICKED, item: evt.item.wall, entity: evt.item , mPos: evt.mPos, viewer2D: this, viewer3D: this.viewer3D});
            } else if (evt.item instanceof CornerView2D) {
                item = evt.item.corner;
                this.__eventDispatcher.dispatchEvent({ type: EVENT_CORNER_2D_CLICKED, item: evt.item.corner, entity: evt.item, viewer2D: this, viewer3D: this.viewer3D });
            } else if (evt.item instanceof RoomView2D) {
                item = evt.item.room;
                this.__eventDispatcher.dispatchEvent({ type: EVENT_ROOM_2D_CLICKED, item: evt.item.room, entity: evt.item });
            } 
            // wallItemView selected
            else if(evt.item instanceof WallItemView2D){
                // if needed dispatch
                this.__eventDispatcher.dispatchEvent({type: EVENT_DOOR_2D_CLICKED, item: evt.item.item, entity: evt.item, viewer2D: this, viewer3D: this.viewer3D});
            }
            // floorItem selected
            else if(evt.item instanceof FloorItemView2D){
                this.__eventDispatcher.dispatchEvent({type: EVENT_FLOOR_ITEM_2D_CLICKED, item:evt.item.item, entity: evt.item, viewer2D: this, viewer3D: this.viewer3D})
            }
            if (this.__mode === floorplannerModes.EDIT_ISLANDS) {
                this.__groupTransformer.visible = true;
                this.__groupTransformer.selected = item;
            }
            this.__currentSelection = item;
        }
    }

    __center(){
        let floorplanCenter = this.__floorplan.getCenter();
        let zoom = this.__floorplanContainer.scale.x;
        let windowSize = new Vector2(this.__currentWidth, this.__currentHeight); 
        let bounds = Dimensioning.cmToPixel(Configuration.getNumericValue(viewBounds)) * zoom;
        // console.log(windowSize.x, windowSize.y);
        let x = (windowSize.x * 0.5)-(floorplanCenter.x*0.5);// - (bounds*0.5);
        let y = (windowSize.y * 0.5)-(floorplanCenter.z*0.5);// - (bounds*0.5);
        this.__floorplanContainer.x = x;
        this.__floorplanContainer.y = y;
        this.__tempWallHolder.x = x;
        this.__tempWallHolder.y = y;
        // 
        this.tempWallItemHolder.x =x;
        this.tempWallItemHolder.y=y;
        //
        this.tempFloorItemHolder.x =x;
        this.tempFloorItemHolder.y=y;
        // console.log(x, y, floorplanCenter);
    }

    __zoomed() {
        let zoom = this.__floorplanContainer.scale.x;
        let bounds = Dimensioning.cmToPixel(Configuration.getNumericValue(viewBounds));// * zoom;
        let maxZoomOut = Math.max(window.innerWidth, window.innerHeight) / bounds;
        zoom = (zoom < maxZoomOut) ? maxZoomOut : (zoom > 60) ? 60 : zoom;
        
        this.__floorplanContainer.scale.x = this.__floorplanContainer.scale.y = zoom;
        this.__tempWallHolder.scale.x = this.__tempWallHolder.scale.y = zoom;

        // 
        this.tempWallItemHolder.scale.x = this.tempWallItemHolder.scale.y = zoom;
        //
        this.tempFloorItemHolder.scale.x = this.tempFloorItemHolder.scale.y = zoom;

        this.__grid2d.gridScale = this.__floorplanContainer.scale.x;
    }

    __panned() {
        let zoom = this.__floorplanContainer.scale.x;
        let bounds = Dimensioning.cmToPixel(Configuration.getNumericValue(viewBounds)) * zoom;

        let xy = new Vector2(this.__floorplanContainer.x, this.__floorplanContainer.y);
        let topleft = new Vector2((-(bounds*0.5)), (-(bounds*0.5)));
        let bottomright = new Vector2(((bounds*0.5)), ((bounds*0.5)));
        
        // let windowSize = new Vector2(window.innerWidth, window.innerHeight);
        let windowSize = new Vector2(this.__currentWidth, this.__currentHeight);        
      
        let xValue = Math.min(-topleft.x, xy.x);
        let yValue = Math.min(-topleft.y, xy.y);

        xValue = Math.max(windowSize.x-bottomright.x, xValue);
        yValue = Math.max(windowSize.y-bottomright.y, yValue);
        
        
        this.__floorplanContainer.x = this.__tempWallHolder.x = xValue;
        this.__floorplanContainer.y = this.__tempWallHolder.y = yValue;

        this.tempWallItemHolder.x = xValue;
        this.tempWallItemHolder.y = yValue;

        this.tempFloorItemHolder.x = xValue;
        this.tempFloorItemHolder.y = yValue;
        // console.log('---------------------------------------------');
        // console.log('CURRENT ZOOM :: ', zoom);
        // console.log('TOP LEFT :: ', topleft);
        // console.log('BOTTOM RIGHT :: ', bottomright);
        // console.log('WINDOW SIZE :: ', windowSize);
        // console.log(`X=${xValue}, Y=${yValue}`);
    }

    __resetFloorplan(evt) {
        this.__mode = floorplannerModes.MOVE;
        this.__groupTransformer.visible = false;
        this.__groupTransformer.selected = null;
        this.__drawExternalFloorplan();
    }

    __redrawFloorplan() {
        let scope = this;
        let i = 0;

        // clear scene
        scope.__entities2D.forEach((entity) => {
            entity.removeFloorplanListener(EVENT_2D_SELECTED, this.__selectionMonitorEvent);
            entity.remove();
        });

        this.__drawBoundary();

        this.__corners2d = [];
        this.__walls2d = [];
        this.__rooms2d = [];
        this.__entities2D = [];

        let rooms = this.__floorplan.getRooms();
        //console.log(this.__floorplanElementsHolder)

        for (i = 0; i < rooms.length; i++) {
            let modelRoom = rooms[i];
            let roomView = new RoomView2D(this.__floorplan, this.__options, modelRoom);
            this.__floorplanElementsHolder.addChild(roomView);
            this.__rooms2d.push(roomView);
            this.__entities2D.push(roomView);
            roomView.interactive = (this.__mode === floorplannerModes.MOVE);
            roomView.addFloorplanListener(EVENT_2D_SELECTED, this.__selectionMonitorEvent);
        }
        for (i = 0; i < this.__floorplan.walls.length; i++) {
            let modelWall = this.__floorplan.walls[i];
            let wallView = new WallView2D(this.__floorplan, this.__options, modelWall);
            this.__floorplanElementsHolder.addChild(wallView);
            this.__walls2d.push(wallView);
            this.__entities2D.push(wallView);
            wallView.interactive = (this.__mode === floorplannerModes.MOVE);
            wallView.addFloorplanListener(EVENT_2D_SELECTED, this.__selectionMonitorEvent);
        }
        for (i = 0; i < this.__floorplan.corners.length; i++) {
            let modelCorner = this.__floorplan.corners[i];
            let cornerView = new CornerView2D(this.__floorplan, this.__options, modelCorner);
            this.__floorplanElementsHolder.addChild(cornerView);
            this.__corners2d.push(cornerView);
            this.__entities2D.push(cornerView);
            cornerView.interactive = (this.__mode === floorplannerModes.MOVE);
            cornerView.addFloorplanListener(EVENT_2D_SELECTED, this.__selectionMonitorEvent);
            modelCorner.removeEventListener(EVENT_MOVED, this.__cornerMovedEvent);
            modelCorner.addEventListener(EVENT_MOVED, this.__cornerMovedEvent);
        }

        // add wallItemView
        let wallArr = this.__floorplan.walls;
        for(let i =0; i<wallArr.length; ++i){
            //console.log(wallArr[i]);
            let wallItems = wallArr[i].__inWallItems;
            // if there are no wallItems in this wall we can skip to the next
            if(wallItems === undefined){
                continue;
            }
            for(let j= 0; j < wallItems.length; ++j ){
                let wall = wallArr[i];
                let wallItemView = new WallItemView2D(this.__floorplan, this.options, wall, wallItems[j]);
                this.__floorplanElementsHolder.addChild(wallItemView);
                this.__entities2D.push(wallItemView);
                wallItemView.interactive = (this.__mode === floorplannerModes.MOVE);
                wallItemView.addFloorplanListener(EVENT_2D_SELECTED, this.__selectionMonitorEvent);
            }
        }

        //add floorItemView
        if(this.roomHelper!== undefined){
            let roomItems = this.roomHelper.__model.__roomItems;
            for(let i=0; i<roomItems.length;++i){
                // consider only floorItems
                if(roomItems[i] instanceof FloorItem){
                    let floorItemView = new FloorItemView2D(this.__floorplan, this.options, roomItems[i], this.viewer3D);
                    this.__floorplanElementsHolder.addChild(floorItemView);
                    this.__entities2D.push(floorItemView);
                    floorItemView.interactive = (this.__mode === floorplannerModes.MOVE);
                    floorItemView.addFloorplanListener(EVENT_2D_SELECTED, this.__selectionMonitorEvent);
                }
            }
        }

        this._handleWindowResize();
    }

    __drawExternalFloorplan() {
        let scope = this;
        let i = 0;
        // clear scene
        scope.__externalEntities2d.forEach((entity) => {
            entity.remove();
        });

        this.__drawBoundary();

        this.__externalCorners2d = [];
        this.__externalWalls2d = [];
        this.__externalRooms2d = [];

        let rooms = this.__floorplan.externalRooms;

        //TODO: add wallItemView
        let wallArr = this.__floorplan.walls;
        for(let i =0; i<wallArr.length; ++i){
            let wallItems = wallArr[i].__inWallItems;
            // if there are no wallItems in this wall we can skip to the next
            if(wallItems === undefined){
                continue;
            }
            for(let j= 0; j < wallItems.length; ++j ){
                let wall = wallArr[i];
                let wallItemView = new WallItemView2D(this.__floorplan, this.options, wall, wallItems[j]);
                this.__floorplanElementsHolder.addChild(wallItemView);
                this.__entities2D.push(wallItemView);
                wallItemView.interactive = (this.__mode === floorplannerModes.Move);
                wallItemView.addFloorplanListener(EVENT_2D_SELECTED, this.__selectionMonitorEvent);
            }
        }

        for (i = 0; i < rooms.length; i++) {
            let modelRoom = rooms[i];
            let roomView = new RoomView2D(this.__floorplan, this.__options, modelRoom);
            this.__floorplanElementsHolder.addChild(roomView);
            this.__externalRooms2d.push(roomView);
            this.__externalEntities2d.push(roomView);
        }
        for (i = 0; i < this.__floorplan.externalWalls.length; i++) {
            let modelWall = this.__floorplan.externalWalls[i];
            let wallView = new WallView2D(this.__floorplan, this.__options, modelWall);
            this.__floorplanElementsHolder.addChild(wallView);
            this.__externalWalls2d.push(wallView);
            this.__externalEntities2d.push(wallView);
        }
        for (i = 0; i < this.__floorplan.externalCorners.length; i++) {
            let modelCorner = this.__floorplan.externalCorners[i];
            let cornerView = new CornerView2D(this.__floorplan, this.__options, modelCorner);
            this.__floorplanElementsHolder.addChild(cornerView);
            this.__externalCorners2d.push(cornerView);
            this.__externalEntities2d.push(cornerView);
        }
        this._handleWindowResize();
    }

    /** */
    _handleWindowResize() {

        let heightMargin = this.__canvasHolder.offsetTop;
        let widthMargin = this.__canvasHolder.offsetLeft;

        let w = (this.__options.resize)? window.innerWidth - widthMargin : this.__canvasHolder.clientWidth;
        let h = (this.__options.resize)? window.innerHeight - heightMargin : this.__canvasHolder.clientHeight;
        
        this.__currentWidth = w;
        this.__currentHeight = h;

        this.renderer.resize(w, h);
        this.renderer.view.style.width = w + 'px';
        this.renderer.view.style.height = h + 'px';
        this.renderer.view.style.display = 'block';
        this.__floorplanContainer.resize(w, h, this.__worldWidth, this.__worldHeight);


        this.renderer.render(this.stage);
        this.__zoomed();
        this.__panned();
    }

    addFloorplanListener(type, listener) {
        this.__eventDispatcher.addEventListener(type, listener);
    }

    removeFloorplanListener(type, listener) {
        this.__eventDispatcher.removeEventListener(type, listener);
    }

    dispose() {
        this.__floorplanContainer.off('zoomed', this.__zoomedEvent);
        this.__floorplanContainer.off('moved', this.__pannedEvent);
        this.__floorplanContainer.off('clicked', this.__selectionMonitorEvent);

        // this.__floorplan.addEventListener(EVENT_UPDATED, (evt) => scope.__redrawFloorplan(evt));
        this.__floorplan.removeEventListener(EVENT_NEW, this.__redrawFloorplanEvent);
        this.__floorplan.removeEventListener(EVENT_DELETED, this.__redrawFloorplanEvent);
        this.__floorplan.removeEventListener(EVENT_LOADED, this.__redrawFloorplanEvent);
        window.removeEventListener('resize', this.__windowResizeEvent);
        window.removeEventListener('orientationchange', this.__windowResizeEvent);
    }
}